<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { computed, ref, watch } from 'vue'
import Breadcrumb from '@/components/Breadcrumb.vue'
import UpdateStep from '@/components/step/UpdateStep.vue'
import CreateStep from '@/components/step/CreateStep.vue'
import StepTree from '@/components/step/StepTree.vue'
import {
  ModuleViewDraftPlaceQuery,
  ModuleViewDraftPlaceQueryVariables,
  GlbStatus,
  ModuleViewDataQueryVariables,
  PermissionOrganization,
  VersionStatus,
} from '@/generated/graphql'
import { useApolloClient, useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import DownloadModuleMbomButton from '@/components/mbom/DownloadModuleMbomButton.vue'
import DownloadModuleWorkInstructionsButton from '@/components/mbom/DownloadModuleWorkInstructionsButton.vue'
import ViewStep from '@/components/step/ViewStep.vue'
import ViewCheckConfigurations from '@/components/checkConfiguration/ViewCheckConfigurations.vue'
import DuplicateStep from '@/components/step/DuplicateStep.vue'
import AddFromLibraryDialog from '@/components/step/AddFromLibraryDialog.vue'
import LibraryStepLock from '@/components/step/LibraryStepLock.vue'
import LibraryStepStatus from '@/components/step/LibraryStepStatus.vue'
import { ComponentExposed } from 'vue-component-type-helpers'
import BopStepModelViewer from '@/components/bop/BopStepModelViewer.vue'
import type BopStepModelViewerType from '@/components/bop/BopStepModelViewer.vue'
import StepProposalsFromFileDialog from '@/components/step/StepProposalsFromFileDialog.vue'
import PromoteStepToModule from '@/components/step/PromoteStepToModule.vue'
import StepPreviewDialog from '@/components/step/StepPreviewDialog.vue'
import useAuthorization from '@/composables/useAuthorization'
import ArchivedConfigurationAlert from '@/components/archive/ArchivedConfigurationAlert.vue'
import { isBopLocked } from '@/utils/bopLock'
import { useHorizResize, useVertResize } from '@/composables/useResizable'

const { t } = useI18n()
const { hasOrgAuthz, reactiveOrgAuthz } = useAuthorization()
const route = useRoute()
const router = useRouter()
const { client } = useApolloClient()

const productId = computed(() => route.params.productId as string)
const moduleId = computed(() => route.params.moduleId as string)
const selectionParam = computed(() => route.params.nodeId as string | undefined)
const bopId = computed(() => route.params.bopId as string)
const siteId = computed(() => route.params.siteId as string)
const configId = computed(() => route.params.productConfigId as string)

type ModuleViewQuery = {
  product: {
    bop: {
      id: string
      versionStatus: VersionStatus
      productConfiguration: {
        id: string
        archived: boolean
        bopsAtSite: Array<{ id: string; version: number }>
      }
      uploads: Array<{ glbStatus: string }>
    }
    module: {
      id: string
      name: string
      abbreviation: string
      nodes: Array<{
        id: string
        parentId?: string | null
        isLibraryReference: boolean
        step: { id: string }
      }>
    }
  }
}

const fetchQuery = useQuery<ModuleViewQuery, ModuleViewDataQueryVariables>(
  gql`
    query ModuleViewData($bopId: ID!, $siteId: ID!, $moduleId: ID!) {
      product {
        bop(id: $bopId) {
          id
          versionStatus
          productConfiguration {
            id
            archived
            bopsAtSite(siteId: $siteId) {
              id
              version
            }
          }
          uploads {
            glbStatus
          }
        }
        module(id: $moduleId) {
          id
          name
          abbreviation
          nodes {
            id
            parentId
            isLibraryReference
            step {
              id
            }
          }
        }
      }
    }
  `,
  () => ({ bopId: bopId.value, siteId: siteId.value, moduleId: moduleId.value }),
)

const selectedNode = computed(() => {
  const nodes = fetchQuery.result.value?.product.module.nodes ?? []
  const param = selectionParam.value
  if (!param) return undefined
  return nodes.find((n) => n.id === param) ?? nodes.find((n) => n.step.id === param)
})
const stepId = computed<string | undefined>(() => selectedNode.value?.step.id)

watch([selectedNode, selectionParam], ([node, param]) => {
  if (node && param && node.id !== param) {
    router.replace({ params: { nodeId: node.id } })
  }
})

// The graph_node_step id of the currently selected library reference (its placement).
const selectedGraphNodeStepId = computed<string>(() =>
  selectedNode.value?.isLibraryReference ? selectedNode.value.id : '',
)

const selectedStepQuery = useQuery<
  { product: { step: { id: string; libraryStep: { id: string } | null } } },
  { id: string }
>(
  gql`
    query ModuleViewSelectedStep($id: ID!) {
      product {
        step(id: $id) {
          id
          libraryStep {
            id
          }
        }
      }
    }
  `,
  () => ({ id: stepId.value || '' }),
  () => ({ enabled: !!stepId.value }),
)
// A linked step, or a library reference saved before library uses became linked steps.
const selectedStepIsLibrary = computed(
  () => !!stepId.value && !!selectedStepQuery.result.value?.product.step.libraryStep,
)

const selectedStepIsSection = computed(() => {
  const nodeId = selectedNode.value?.id
  const nodes = fetchQuery.result.value?.product.module.nodes ?? []
  return !!nodeId && nodes.some((n) => n.parentId === nodeId)
})

const isReleased = computed(
  () => fetchQuery.result.value?.product.bop.versionStatus == VersionStatus.Released,
)
const isLocked = computed(() => isBopLocked(fetchQuery.result.value?.product.bop))
// Only the newest version of a BOP can take a library update (Jasper, BASE-315).
const isLatestBopVersion = computed(
  () =>
    [...(fetchQuery.result.value?.product.bop.productConfiguration.bopsAtSite ?? [])].sort(
      (a, b) => b.version - a.version,
    )[0]?.id == bopId.value,
)
const hasGlbFile = computed(() => {
  const uploads = fetchQuery.result.value?.product.bop.uploads ?? []
  return uploads.some(
    (u) =>
      u.glbStatus === GlbStatus.Done ||
      u.glbStatus === GlbStatus.Queued ||
      u.glbStatus === GlbStatus.Converting,
  )
})
const module = computed(() => fetchQuery.result.value?.product.module)
const canMbom = computed(
  () => !!module.value && isReleased.value && hasOrgAuthz(PermissionOrganization.OrgMbomRead),
)

type State = 'initial' | 'update' | 'create' | 'view'
const state = ref<State>('initial')
const createParentNodeId = ref<string>()
const createAfterNodeId = ref<string>()
function createNode(parentNodeId?: string, afterNodeId?: string) {
  createParentNodeId.value = parentNodeId
  createAfterNodeId.value = afterNodeId
  state.value = 'create'
}
async function onStepCreated(newStepId: string) {
  const afterNodeId = createAfterNodeId.value
  createAfterNodeId.value = undefined
  onSelectStep(newStepId)
  if (afterNodeId) {
    await stepTree.value?.placeAfter(newStepId, afterNodeId)
  }
}
watch(
  [stepId, isLocked, reactiveOrgAuthz(PermissionOrganization.OrgProductWrite)],
  () => {
    state.value = stepId.value
      ? isLocked.value || !hasOrgAuthz(PermissionOrganization.OrgProductWrite)
        ? 'view'
        : 'update'
      : 'initial'
  },
  { immediate: true },
)

const stepTree = ref<ComponentExposed<typeof StepTree>>()
const modelViewer = ref<ComponentExposed<typeof BopStepModelViewerType>>()
const refreshOutsideViewer = () => {
  fetchQuery.refetch()
  selectedStepQuery.refetch()
  stepTree.value?.refresh()
}
const refresh = () => {
  refreshOutsideViewer()
  if (hasGlbFile.value) modelViewer.value?.refresh()
}

const onUnlinked = () => refresh()

// The new draft is a copy of this release, so the module and the step sit at the same place in it.
const openInDraft = async (draftBopId: string) => {
  const { data } = await client.query<
    ModuleViewDraftPlaceQuery,
    ModuleViewDraftPlaceQueryVariables
  >({
    query: gql`
      query ModuleViewDraftPlace($releasedBopId: ID!, $draftBopId: ID!) {
        product {
          released: bop(id: $releasedBopId) {
            nodes {
              module {
                id
                nodes {
                  step {
                    id
                  }
                }
              }
            }
          }
          draft: bop(id: $draftBopId) {
            nodes {
              module {
                id
                nodes {
                  id
                }
              }
            }
          }
        }
      }
    `,
    variables: { releasedBopId: bopId.value, draftBopId },
    fetchPolicy: 'network-only',
  })
  const moduleIndex = data.product.released.nodes.findIndex((n) => n.module.id == moduleId.value)
  const stepIndex = data.product.released.nodes[moduleIndex].module.nodes.findIndex(
    (n) => n.step.id == stepId.value,
  )
  const draftModule = data.product.draft.nodes[moduleIndex].module
  await router.push({
    params: {
      bopId: draftBopId,
      moduleId: draftModule.id,
      nodeId: draftModule.nodes[stepIndex].id,
    },
  })
}

const notifyLibraryUpdate = ref(false)
const libraryUpdateMessage = ref('')
const onLibraryStepUpdated = async (updatedBopId: string) => {
  const isDraftCreated = updatedBopId != bopId.value
  libraryUpdateMessage.value = t(
    isDraftCreated
      ? 'component.acceptLibraryStepUpdate.draftCreated'
      : 'component.acceptLibraryStepUpdate.updateApplied',
  )
  // Before navigating: the draft exists even if opening it fails.
  notifyLibraryUpdate.value = true
  if (isDraftCreated) await openInDraft(updatedBopId)
  else refresh()
}

const onLibraryStepDeleted = () => {
  router.push({ params: { nodeId: undefined } })
  refresh()
}

// Accepts either a node id (tree selection) or a step id (freshly created step); both resolve to a
// placement via selectedNode.
const onSelectStep = (id?: string) => {
  fetchQuery.refetch()
  stepTree.value?.refresh()
  if (id) router.push({ params: { nodeId: id } })
}
const onStepDeleted = async () => {
  // Refresh the tree first so we can auto-select a remaining step (relative
  // navigation keeps the other route params; the watcher updates `state`).
  await stepTree.value?.refresh()
  await router.push({ params: { nodeId: stepTree.value?.firstNodeId() } })
  fetchQuery.refetch()
  if (hasGlbFile.value) modelViewer.value?.refresh()
}

const duplicateStepDialog = ref<ComponentExposed<typeof DuplicateStep>>()
const doDuplicate = async () => duplicateStepDialog.value?.open()
const onDuplicated = (id: string) => {
  refresh()
  router.push({ params: { nodeId: id } })
}

const addFromLibraryDialog = ref<ComponentExposed<typeof AddFromLibraryDialog>>()
const doAddFromLibrary = async () => addFromLibraryDialog.value?.open()

const proposalsFromFileDialog = ref<ComponentExposed<typeof StepProposalsFromFileDialog>>()
const onProposeFromMedia = async () => proposalsFromFileDialog.value?.open()

const promoteDialog = ref<ComponentExposed<typeof PromoteStepToModule>>()
const doPromote = async () => promoteDialog.value?.open()
const onPromoted = () => router.push({ name: 'bopProcess' })

const updateStep = ref<ComponentExposed<typeof UpdateStep>>()
const previewOpen = ref(false)
const previewStepId = ref<string>()
const openPreview = (fromStepId?: string) => {
  previewStepId.value = fromStepId
  previewOpen.value = true
}
// Keep the tree and editor on the step being previewed without stacking history entries.
const onPreviewStepId = (id: string) => router.replace({ params: { nodeId: id } })

// Resizable splits
const {
  pct: leftColPct,
  containerEl: mainRow,
  startDrag: startHorizDrag,
} = useHorizResize(33, 20, 75)
const { pct: viewerPct, containerEl: leftCol, startDrag: startVertDrag } = useVertResize(50, 15, 75)
</script>

<template>
  <breadcrumb />
  <archived-configuration-alert :product-configuration-id="configId" />
  <div v-if="canMbom && !!module" class="d-flex justify-end ga-2 mb-2">
    <download-module-mbom-button
      :bop-id="bopId"
      :module="module"
      short
      variant="elevated"
      color="primary"
    />
    <download-module-work-instructions-button
      :bop-id="bopId"
      :module="module"
      short
      variant="elevated"
      color="primary"
    />
  </div>

  <div ref="mainRow" class="mt-1 module-layout">
    <div ref="leftCol" class="left-col" :style="{ width: leftColPct + '%' }">
      <div class="overflow-y-auto overflow-x-hidden" style="flex: 1; min-height: 0">
        <step-tree
          ref="stepTree"
          :bop-id="bopId"
          :module-id="moduleId"
          :product-id="productId"
          :is-latest-bop-version="isLatestBopVersion"
          :selected-node-id="selectedNode?.id"
          @create-node="createNode"
          @copy-node="doDuplicate"
          @add-from-library="doAddFromLibrary"
          @propose-from-media="onProposeFromMedia"
          @preview="openPreview()"
          @select-node-id="onSelectStep"
        />
      </div>

      <template v-if="hasGlbFile">
        <div class="resize-handle-h" @mousedown="startVertDrag" />

        <div :style="{ height: viewerPct + '%', minHeight: '150px' }">
          <bop-step-model-viewer
            ref="modelViewer"
            :bop-id="bopId"
            :module-id="moduleId"
            :step-id="stepId"
            :node-id="selectedNode?.id"
            :can-save-viewport="!isLocked && !selectedStepIsLibrary"
            :allocatable="state === 'update' && !selectedStepIsLibrary"
            show-active-ebom-label
            show-controls-help
            show-ebom-tree
            @allocated="refreshOutsideViewer"
          />
        </div>
      </template>
    </div>

    <div class="resize-handle-v" @mousedown="startHorizDrag" />

    <div class="overflow-y-auto pr-3" style="flex: 1; min-height: 0">
      <v-empty-state v-if="state == 'initial'" icon="account_tree" text="Select a step" />
      <library-step-status
        v-if="!!stepId && !selectedStepIsLibrary && (state == 'update' || state == 'view')"
        :step-id="stepId"
      />
      <create-step
        v-if="state == 'create'"
        :site-id="siteId"
        :module-id="moduleId"
        :product-id="productId"
        :parent-node-id="createParentNodeId"
        @created="onStepCreated"
      />
      <update-step
        v-if="!!stepId && state == 'update' && !selectedStepIsLibrary"
        ref="updateStep"
        :step-id="stepId"
        :site-id="siteId"
        :module-id="moduleId"
        :bop-id="bopId"
        :has-glb-file="hasGlbFile"
        :is-section="selectedStepIsSection"
        @updated="refresh"
        @deleted="onStepDeleted"
        @preview="openPreview(stepId)"
        @promote="doPromote"
      />
      <promote-step-to-module
        v-if="!!stepId && !!selectedNode && state == 'update'"
        ref="promoteDialog"
        :bop-id="bopId"
        :module-id="moduleId"
        :step-id="stepId"
        :graph-node-step-id="selectedNode.id"
        @promoted="onPromoted"
      />
      <library-step-lock
        v-if="!!stepId && selectedStepIsLibrary && (state == 'update' || state == 'view')"
        :step-id="stepId"
        :module-id="moduleId"
        :graph-node-step-id="selectedGraphNodeStepId || undefined"
        :bop-id="bopId"
        :site-id="siteId"
        :is-latest-bop-version="isLatestBopVersion"
        :can-unlink="state == 'update'"
        @unlinked="onUnlinked"
        @deleted="onLibraryStepDeleted"
        @updated="onLibraryStepUpdated"
      />
      <duplicate-step
        ref="duplicateStepDialog"
        :product-id="productId"
        :target-bop-id="bopId"
        :target-module-id="moduleId"
        :initial-site-id="siteId"
        :initial-config-id="configId"
        :initial-step-id="stepId"
        @duplicated="onDuplicated"
      />
      <add-from-library-dialog
        ref="addFromLibraryDialog"
        :module-id="moduleId"
        @added="onSelectStep"
      />
      <step-preview-dialog
        v-model="previewOpen"
        :step-id="previewStepId"
        :bop-id="bopId"
        :module-id="moduleId"
        :draft-step-id="stepId"
        :draft="updateStep?.draft"
        @select-step-id="onPreviewStepId"
      />
      <step-proposals-from-file-dialog
        v-if="hasOrgAuthz(PermissionOrganization.OrgProductWrite)"
        ref="proposalsFromFileDialog"
        :product-id="productId"
        :module-id="moduleId"
        :bop-id="bopId"
        :site-id="siteId"
        @created="onSelectStep"
      />
      <template v-if="!!stepId && state == 'view' && !selectedStepIsLibrary">
        <view-step :step-id="stepId" />
        <h2 class="mt-10">{{ t('entity.checkConfiguration.plural') }}</h2>
        <view-check-configurations :step-id="stepId" />
      </template>

      <div class="pb-8" />
    </div>
  </div>
  <v-snackbar v-model="notifyLibraryUpdate">{{ libraryUpdateMessage }}</v-snackbar>
</template>

<style scoped lang="scss">
.module-layout {
  display: flex;
  height: calc(100vh - 120px);
  min-height: 0;
  overflow: hidden;
}

.left-col {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}
</style>
